import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { Activity } from "lucide-react";

const trackerValue = (t, tv) => (t.type === "yesno" ? (tv.value ? 1 : 0) : tv.value);
const pickCls = (idx, has) => {
  if (idx >= 0) return "";
  return has ? "bg-stone-100 text-stone-600 hover:bg-stone-200" : "bg-stone-50 text-stone-300";
};
const unitLabel = (t) => {
  if (t.type === "yesno") return "· yes/no";
  if (t.unit) return `· ${t.unit}`;
  return t.type === "scale" ? "· /10" : "";
};

const COLORS = ["#059669", "#d9467a", "#2563EB", "#2f7a46", "#7C3AED"];
const MAX_PICK = 3;

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function WellbeingTrends({ symptoms, defs, trackers = [], title = "Wellbeing trends", height = 200 }) {
  const [picked, setPicked] = useState(["mood", "anxiety", "sleep"]);
  const rows = [...symptoms].sort((a, b) => a.created_at.localeCompare(b.created_at)).slice(-30);
  const data = rows.map((s) => ({ date: fmtDate(s.created_at), ...(s.scales || { mood: s.mood, anxiety: s.anxiety }) }));
  const present = new Set(rows.flatMap((s) => Object.keys(s.scales || { mood: 1, anxiety: 1 })));

  const toggle = (k) => setPicked(picked.includes(k) ? picked.filter((x) => x !== k) : [...picked.slice(-(MAX_PICK - 1)), k]);

  const trackerSeries = trackers
    .map((t) => ({
      t,
      data: rows
        .map((s) => {
          const tv = (s.tracker_values || []).find((x) => x.tracker_id === t.id);
          return tv ? { date: fmtDate(s.created_at), value: trackerValue(t, tv) } : null;
        })
        .filter(Boolean),
    }))
    .filter((x) => x.data.length > 0);

  return (
    <div className="warm-card p-6" data-testid="wellbeing-trends">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-5 h-5 text-emerald-700" />
        <h3 className="font-display text-lg font-bold text-stone-900">{title}</h3>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="trend-scale-picker">
        {defs.map((d) => {
          const idx = picked.indexOf(d.key);
          const has = present.has(d.key);
          return (
            <button
              key={d.key}
              type="button"
              data-testid={`trend-pick-${d.key}`}
              onClick={() => toggle(d.key)}
              disabled={!has}
              style={idx >= 0 ? { backgroundColor: COLORS[idx], color: "#fff" } : undefined}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition ${pickCls(idx, has)}`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
      {data.length === 0 ? (
        <div className="text-sm text-stone-500 py-10 text-center">No check-ins yet — your trends will appear here.</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D8" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#78716C" }} />
            <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#78716C" }} />
            <Tooltip />
            {picked.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k} name={defs.find((d) => d.key === k)?.label || k} stroke={COLORS[i]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {trackerSeries.length > 0 && (
        <div className="mt-4 pt-4 border-t border-stone-100 grid sm:grid-cols-2 gap-4" data-testid="tracker-trends">
          {trackerSeries.map(({ t, data: td }) => (
            <div key={t.id} data-testid={`tracker-trend-${t.id}`}>
              <div className="text-xs font-bold text-stone-700 mb-1">
                {t.name} <span className="text-stone-400 font-normal">{unitLabel(t)}</span>
              </div>
              <ResponsiveContainer width="100%" height={110}>
                {t.type === "yesno" ? (
                  <BarChart data={td}>
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#78716C" }} />
                    <YAxis domain={[0, 1]} ticks={[0, 1]} tickFormatter={(v) => (v ? "Yes" : "No")} tick={{ fontSize: 9, fill: "#78716C" }} width={28} />
                    <Tooltip formatter={(v) => (v ? "Yes" : "No")} />
                    <Bar dataKey="value" fill="#059669" radius={[4, 4, 0, 0]} />
                  </BarChart>
                ) : (
                  <LineChart data={td}>
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#78716C" }} />
                    <YAxis domain={t.type === "scale" ? [0, 10] : ["auto", "auto"]} tick={{ fontSize: 9, fill: "#78716C" }} width={28} />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke="#d9467a" strokeWidth={2} dot={{ r: 2.5 }} />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
